/**
 * NewInvoiceWizard — dental-first billing workflow.
 *
 * Replaces the legacy "generic CRUD invoice form" with a 1100 px wizard that:
 *   1. Searches for a patient
 *   2. Pulls their billing context in a single request:
 *        • completed treatments that haven't been invoiced yet
 *        • outstanding / wallet / last visit / last payment / unpaid count
 *   3. Lets the receptionist tick which completed treatments to bill —
 *      line items auto-populate (name + variant, tooth chips, cost, qty).
 *   4. Supports "+ Add manual item" with procedure-catalog autocomplete and
 *      FDI 11–48 tooth multi-select.
 *   5. Structured line-item table with per-row discount %.
 *   6. Sticky footer.  On Generate → opens the existing CollectPaymentDialog
 *      so billing + payment happen in a single flow.
 *   7. Linked treatments get an `invoice_id` on the backend so they don't
 *      appear in "ready to bill" next time.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import {
  Search, User, Plus, Trash2, ChevronDown, ClipboardList, Wallet,
  Receipt, Calendar as CalendarIcon, Sparkles, Loader2,
} from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import CollectPaymentDialog from "@/components/app/CollectPaymentDialog";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

// Which catalog categories imply a tooth is required. Non-tooth procedures
// (Consultation, Scaling, X-Ray, Fluoride…) live under Preventive/Other.
const TOOTH_REQUIRED_CATEGORIES = new Set([
  "Restorative", "Endodontic", "Prosthetic", "Surgical", "Cosmetic",
]);
const RECENT_KEY = "viyora.wizard.recentProcedures.v1";
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const pushRecent = (id) => {
  if (!id) return;
  const list = [id, ...readRecent().filter((x) => x !== id)].slice(0, 8);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch {}
};

// FDI numbering system — quadrants: UR(11-18) UL(21-28) LL(31-38) LR(41-48)
const FDI_TEETH = [
  ...[18,17,16,15,14,13,12,11],
  ...[21,22,23,24,25,26,27,28],
  ...[48,47,46,45,44,43,42,41],
  ...[31,32,33,34,35,36,37,38],
].map(String);

/* ------------------------------------------------------------------ */

export default function NewInvoiceWizard({ open, onOpenChange, patients = [], onSaved }) {
  const [patient, setPatient] = useState(null);
  const [ctx, setCtx] = useState(null);
  const [loadingCtx, setLoadingCtx] = useState(false);
  const [items, setItems] = useState([]);          // { key, treatment_id, name, tooth[], qty, price, discount_pct, source }
  const [discountType, setDiscType] = useState("none");     // none | percentage | fixed
  const [discountValue, setDiscValue] = useState(0);
  const [gstMode, setGstMode] = useState(false);
  const [gstRate, setGstRate] = useState(18);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [collectFor, setCollectFor] = useState(null);   // { id, name } — opens payment drawer

  // Memoised so the array reference is stable across renders and doesn't cause
  // BillableTreatmentsCard to re-mount its picked-state on every keystroke.
  const pickedTreatmentIds = useMemo(
    () => items.map((it) => it.treatment_id).filter(Boolean),
    [items],
  );

  // ---- Reset when re-opened
  useEffect(() => {
    if (!open) return;
    setPatient(null); setCtx(null); setItems([]);
    setDiscType("none"); setDiscValue(0); setGstMode(false); setNotes("");
  }, [open]);

  // ---- Procedure catalog (for the manual-add typeahead + default prices)
  useEffect(() => {
    if (!open || catalog.length) return;
    api.get("/procedure-catalog").then(({ data }) => setCatalog(data || []));
  }, [open, catalog.length]);

  // ---- Load patient billing context whenever patient changes
  const loadCtx = useCallback(async (pid) => {
    setLoadingCtx(true);
    try {
      const { data } = await api.get(`/patients/${pid}/billing-context`);
      setCtx(data);
    } catch (err) {
      toast.error(apiError(err, "Could not load patient context"));
      setCtx(null);
    } finally { setLoadingCtx(false); }
  }, []);

  useEffect(() => {
    if (patient?.id) loadCtx(patient.id);
  }, [patient?.id, loadCtx]);

  // ---- Line-item helpers
  const addFromTreatment = (t) => {
    const label = t.variant ? `${t.procedure} · ${t.variant}` : t.procedure;
    setItems((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        treatment_id: t.id,
        name: label,
        tooth: t.tooth ? String(t.tooth).split(/[,/ ]+/).filter(Boolean) : [],
        qty: 1,
        price: Number(t.cost || 0),
        discount_pct: 0,
        source: "visit",
      },
    ]);
  };
  const addManualRow = () => setItems((prev) => [
    ...prev,
    { key: crypto.randomUUID(), treatment_id: null, catalog_id: null, category: null,
      requires_tooth: true, name: "", tooth: [], qty: 1, price: 0, discount_pct: 0, source: "manual" },
  ]);
  const patchItem = (key, patch) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, ...patch } : it)));
  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  // ---- Totals
  const totals = useMemo(() => {
    const rowTotals = items.map((it) => {
      const gross = Number(it.qty || 0) * Number(it.price || 0);
      const rowDisc = gross * (Number(it.discount_pct || 0) / 100);
      return { key: it.key, gross, net: Math.max(0, gross - rowDisc) };
    });
    const subtotal = rowTotals.reduce((s, r) => s + r.net, 0);
    const globalDisc =
      discountType === "percentage" ? subtotal * (Number(discountValue) / 100) :
      discountType === "fixed"      ? Math.min(Number(discountValue), subtotal) :
      0;
    const taxable = Math.max(0, subtotal - globalDisc);
    const tax = gstMode ? taxable * (Number(gstRate) / 100) : 0;
    const grand = taxable + tax;
    return { rowTotals: Object.fromEntries(rowTotals.map((r) => [r.key, r])), subtotal, globalDisc, taxable, tax, grand };
  }, [items, discountType, discountValue, gstMode, gstRate]);

  // ---- Submit
  const submit = async () => {
    if (!patient) return toast.error("Select a patient");
    if (items.length === 0) return toast.error("Add at least one item");
    if (items.some((it) => !it.name.trim())) return toast.error("Every row needs a treatment name");

    setBusy(true);
    try {
      const payloadItems = items.map((it) => {
        const gross = Number(it.qty) * Number(it.price);
        const rowDisc = gross * (Number(it.discount_pct || 0) / 100);
        return {
          name: it.tooth.length ? `${it.name} — Tooth ${it.tooth.join(", ")}` : it.name,
          quantity: Number(it.qty),
          amount: Number(it.price),
          discount_percent: Number(it.discount_pct || 0),
          discount_amount: Math.round(rowDisc * 100) / 100,
          net_amount: Math.round((gross - rowDisc) * 100) / 100,
          tooth: it.tooth,
        };
      });
      const treatmentIds = items.map((it) => it.treatment_id).filter(Boolean);
      const { data: inv } = await api.post("/invoices", {
        patient_id: patient.id,
        items: payloadItems,
        discount_type: discountType,
        discount_value: Number(discountValue) || 0,
        gst_rate: gstMode ? Number(gstRate) : 0,
        invoice_type: gstMode ? "gst" : "non_gst",
        notes: notes || null,
        treatment_ids: treatmentIds.length ? treatmentIds : null,
      });
      toast.success(`Invoice ${inv.invoice_number} created`);
      onSaved?.(inv);
      // Chain into the payment drawer — same workflow.
      setCollectFor({ id: patient.id, name: patient.name, invoiceId: inv.id });
    } catch (err) {
      toast.error(apiError(err, "Failed to create invoice"));
    } finally { setBusy(false); }
  };

  const closeAll = () => { setCollectFor(null); onOpenChange?.(false); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="bg-white p-0 gap-0 max-w-[1200px] w-[96vw] max-h-[92vh] flex flex-col overflow-hidden"
          data-testid="new-invoice-wizard"
          onPointerDownOutside={(e) => {
            if (e.target?.closest?.("[data-viyora-popover]")) e.preventDefault();
          }}
          onInteractOutside={(e) => {
            if (e.target?.closest?.("[data-viyora-popover]")) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            // Radix's focus-scope tries to yank focus back to the modal the
            // instant it lands on a portal element — that steals the click's
            // native focus target and can nullify state updates in some
            // browsers.  Preserving focus on the portal fixes it.
            if (e.target?.closest?.("[data-viyora-popover]")) e.preventDefault();
          }}
        >
          {/* Header — Radix DialogContent already renders its own close X in
              the top-right, so we don't add another one here. */}
          <div className="px-6 py-4 border-b border-[#EFEADC]">
            <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-ink-soft">New invoice</div>
            <div className="font-display text-2xl text-ink">Bill a visit</div>
          </div>

          {/* Body — scrolls */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Step 1 — Patient */}
            <PatientPicker patients={patients} value={patient} onChange={setPatient} />

            {/* Step 2 — Summary strip + billable treatments */}
            {patient && (
              <>
                {loadingCtx ? (
                  <div className="py-8 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-ink-soft" /></div>
                ) : ctx ? (
                  <>
                    <PatientSummaryStrip summary={ctx.summary} />
                    <BillableTreatmentsCard
                      treatments={ctx.billable_treatments}
                      pickedIds={pickedTreatmentIds}
                      onPick={addFromTreatment}
                      onPickAll={(list) => list.forEach(addFromTreatment)}
                      mode={ctx.billing_mode}
                    />
                  </>
                ) : null}
              </>
            )}

            {/* Step 3 — Line items table */}
            <ItemsTable
              items={items} rowTotals={totals.rowTotals}
              catalog={catalog}
              onPatch={patchItem} onRemove={removeItem} onAddManual={addManualRow}
            />

            {/* Step 4 — Discount / tax / notes */}
            {items.length > 0 && (
              <div className="grid md:grid-cols-3 gap-4 pt-2">
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Global discount</label>
                  <div className="mt-1.5 flex gap-2">
                    <select value={discountType} onChange={(e) => setDiscType(e.target.value)}
                            className="rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm bg-white">
                      <option value="none">None</option>
                      <option value="percentage">%</option>
                      <option value="fixed">₹ flat</option>
                    </select>
                    <input type="number" min={0} disabled={discountType === "none"} placeholder="0"
                           value={discountValue} onChange={(e) => setDiscValue(Number(e.target.value))}
                           className="flex-1 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm disabled:bg-linen"/>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">GST</label>
                  <div className="mt-1.5 flex gap-2 items-center">
                    <label className="text-xs inline-flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" className="accent-pine" checked={gstMode}
                             onChange={(e) => setGstMode(e.target.checked)} />
                      GST invoice
                    </label>
                    {gstMode && (
                      <input type="number" min={0} max={28} step={0.5}
                             value={gstRate} onChange={(e) => setGstRate(Number(e.target.value))}
                             className="w-24 rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm" />
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Notes (optional)</label>
                  <input value={notes} onChange={(e) => setNotes(e.target.value)}
                         placeholder="Internal notes"
                         className="mt-1.5 w-full rounded-xl border border-[#E8EAE9] px-3 py-2 text-sm" />
                </div>
              </div>
            )}
          </div>

          {/* Sticky footer */}
          <div className="px-6 py-4 border-t border-[#EFEADC] bg-linen/40 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-6 text-sm">
              <div><span className="text-ink-soft">Subtotal</span> <span className="font-mono text-ink font-semibold ml-2">{INR(totals.subtotal)}</span></div>
              {totals.globalDisc > 0 && <div><span className="text-ink-soft">Discount</span> <span className="font-mono text-ink font-semibold ml-2">− {INR(totals.globalDisc)}</span></div>}
              {gstMode && <div><span className="text-ink-soft">GST @ {gstRate}%</span> <span className="font-mono text-ink font-semibold ml-2">{INR(totals.tax)}</span></div>}
              <div className="pl-6 border-l border-ink-faint">
                <span className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Total</span>
                <div className="font-display text-2xl text-ink font-bold tabular-nums">{INR(totals.grand)}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => onOpenChange(false)}
                      className="px-5 py-2.5 rounded-full text-sm font-semibold text-ink border border-ink-faint hover:bg-white"
                      data-testid="wizard-cancel">
                Cancel
              </button>
              <button onClick={submit} disabled={busy || !patient || items.length === 0}
                      className="px-6 py-2.5 rounded-full text-sm font-semibold bg-ink text-white hover:bg-ink-mid disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                      data-testid="wizard-generate">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
                {busy ? "Generating…" : "Generate invoice → collect payment"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {collectFor && (
        <CollectPaymentDialog
          open={!!collectFor}
          onOpenChange={(v) => { if (!v) closeAll(); }}
          patientId={collectFor.id}
          patientName={collectFor.name}
          onSaved={() => { onSaved?.(); closeAll(); }}
        />
      )}
    </>
  );
}


/* ---------- Sub-components ------------------------------------------- */

function PatientPicker({ patients, value, onChange }) {
  const [query, setQuery] = useState("");
  const [openList, setOpenList] = useState(false);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return patients.filter((p) =>
      p.name?.toLowerCase().includes(q) || p.phone?.includes(q),
    ).slice(0, 8);
  }, [query, patients]);

  if (value) {
    return (
      <div className="flex items-center gap-3 p-4 rounded-2xl border border-[#EFEADC] bg-linen/30" data-testid="wizard-patient-picked">
        <div className="w-11 h-11 rounded-full bg-pine/10 text-pine grid place-items-center">
          <User className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Patient</div>
          <div className="font-semibold text-ink text-lg leading-tight">{value.name}</div>
          {value.phone && <div className="text-xs text-ink-mid">{value.phone}</div>}
        </div>
        <button onClick={() => { onChange(null); setQuery(""); }}
                className="text-xs text-ink-mid hover:text-ink underline underline-offset-2"
                data-testid="wizard-patient-change">
          Change
        </button>
      </div>
    );
  }

  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Step 1 · Select patient</label>
      <div className="mt-2 relative">
        <Search className="w-4 h-4 text-ink-soft absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={query} onChange={(e) => { setQuery(e.target.value); setOpenList(true); }}
               onFocus={() => setOpenList(true)}
               placeholder="Search by name or phone…"
               data-testid="wizard-patient-search"
               className="w-full pl-10 pr-3 py-3 rounded-xl border border-[#E8EAE9] text-sm focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10" />
        {openList && results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full rounded-xl border border-[#EFEADC] bg-white shadow-lg overflow-hidden max-h-72 overflow-y-auto">
            {results.map((p) => (
              <button key={p.id} onClick={() => { onChange(p); setQuery(""); setOpenList(false); }}
                      data-testid={`wizard-patient-hit-${p.id}`}
                      className="w-full text-left px-4 py-2.5 hover:bg-linen border-b border-[#F0EBDD] last:border-b-0">
                <div className="font-medium text-ink text-sm">{p.name}</div>
                {p.phone && <div className="text-xs text-ink-mid">{p.phone}</div>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function PatientSummaryStrip({ summary }) {
  const s = summary || {};
  const Item = ({ label, value, tone = "ink", icon: Icon }) => (
    <div className="flex-1 min-w-[130px] px-4 py-3 rounded-xl border border-[#EFEADC] bg-white">
      <div className="text-[10px] uppercase tracking-[0.14em] text-ink-soft font-semibold flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`mt-1 font-display font-semibold text-lg tabular-nums ${
        tone === "amber" ? "text-amber-900" : tone === "green" ? "text-emerald-700" : "text-ink"
      }`}>{value}</div>
    </div>
  );
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div className="flex flex-wrap gap-3" data-testid="wizard-patient-summary">
      <Item label="Outstanding"       value={INR(s.outstanding)}       tone={s.outstanding > 0 ? "amber" : "ink"} icon={Receipt} />
      <Item label="Wallet balance"    value={INR(s.wallet_balance)}    tone={s.wallet_balance > 0 ? "green" : "ink"} icon={Wallet} />
      <Item label="Unpaid invoices"   value={s.unpaid_invoice_count || 0} icon={ClipboardList} />
      <Item label="Last visit"        value={fmtDate(s.last_visit_at)} icon={CalendarIcon} />
      <Item label="Last payment"      value={s.last_payment_amount ? INR(s.last_payment_amount) : "—"} />
    </div>
  );
}


function BillableTreatmentsCard({ treatments, pickedIds, onPick, onPickAll, mode }) {
  if (!treatments || treatments.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#EFEADC] bg-white p-5" data-testid="wizard-billable-empty">
        <div className="flex items-center gap-2 text-ink-soft text-sm">
          <Sparkles className="w-4 h-4" />
          {mode === "manual"
            ? "Billing preference is set to Manual — add items manually below."
            : "No completed treatments waiting to be billed. Add items manually below."}
        </div>
      </div>
    );
  }
  const pickedSet = new Set(pickedIds);
  const remaining = treatments.filter((t) => !pickedSet.has(t.id));
  const upfrontMode = mode === "upfront";

  return (
    <div className="rounded-2xl border border-pine/25 bg-pine/[0.03] p-5" data-testid="wizard-billable-card">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-pine flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            {upfrontMode ? "Full treatment plan · ready to bill" : "Completed & ready to bill"}
          </div>
          <div className="text-sm text-ink font-medium mt-0.5">
            {treatments.length} treatment{treatments.length === 1 ? "" : "s"} for this patient
            {upfrontMode && <span className="text-ink-mid"> · billed upfront per your preference</span>}
          </div>
        </div>
        {remaining.length > 1 && (
          <button onClick={() => onPickAll(remaining)}
                  data-testid="wizard-add-all"
                  className="text-xs font-semibold text-pine hover:underline">
            Add all {remaining.length} →
          </button>
        )}
      </div>
      <div className="space-y-2">
        {treatments.map((t) => {
          const picked = pickedSet.has(t.id);
          const label = t.variant ? `${t.procedure} · ${t.variant}` : t.procedure;
          const date = t.date ? new Date(t.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";
          const isFuture = t.status === "planned" || t.status === "in_progress";
          return (
            <button key={t.id} type="button" onClick={() => !picked && onPick(t)}
                    disabled={picked}
                    data-testid={`wizard-billable-row-${t.id}`}
                    className={`w-full text-left px-4 py-2.5 rounded-xl border transition flex items-center gap-3 ${
                      picked
                        ? "border-emerald-200 bg-emerald-50/60 cursor-default"
                        : "border-[#EFEADC] bg-white hover:border-pine hover:shadow-sm"
                    }`}>
              <div className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${
                picked ? "border-emerald-500 bg-emerald-500 text-white" : "border-ink-faint"
              }`}>
                {picked && <svg viewBox="0 0 20 20" className="w-3 h-3 fill-current"><path d="M7 13.5L4 10.5l1.4-1.4L7 10.7l7.6-7.6L16 4.5z"/></svg>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink flex items-center gap-2 flex-wrap">
                  {label}
                  {isFuture && (
                    <span className="text-[9px] uppercase tracking-[0.14em] font-bold px-1.5 py-0.5 rounded-md bg-[#EDE7F6] text-[#5B21B6]">
                      Future session
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-ink-mid mt-0.5 flex items-center gap-2 flex-wrap">
                  {t.tooth && <ToothChips value={String(t.tooth).split(/[,/ ]+/).filter(Boolean)} readOnly />}
                  <span>· {date}</span>
                </div>
              </div>
              <div className="font-mono text-sm text-ink shrink-0">{INR(t.cost)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}


function ItemsTable({ items, rowTotals, catalog, onPatch, onRemove, onAddManual }) {
  const cellCls = "px-4 py-3 align-middle";
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-ink-soft">Line items</label>
        <button onClick={onAddManual} data-testid="wizard-add-manual"
                className="text-xs font-semibold inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-ink text-ink hover:bg-ink hover:text-white transition">
          <Plus className="w-3.5 h-3.5" /> Add manual item
        </button>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EFEADC] bg-white/40 p-8 text-center text-sm text-ink-soft" data-testid="wizard-items-empty">
          Pick a completed treatment above, or add a manual line to get started.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#EFEADC] overflow-visible">
          <table className="w-full text-sm">
            <thead className="bg-linen/60 text-[10px] uppercase tracking-widest text-ink-soft">
              <tr>
                <th className={`${cellCls} text-left`}>Treatment</th>
                <th className={`${cellCls} text-left w-[220px]`}>Tooth</th>
                <th className={`${cellCls} text-right w-[90px]`}>Qty</th>
                <th className={`${cellCls} text-right w-[130px]`}>Unit price</th>
                <th className={`${cellCls} text-right w-[110px]`}>Disc %</th>
                <th className={`${cellCls} text-right w-[130px]`}>Total</th>
                <th className={`${cellCls} w-[56px]`}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFEADC]">
              {items.map((it) => {
                const total = rowTotals[it.key]?.net || 0;
                const isManual = it.source === "manual";
                const hideTooth = it.requires_tooth === false;
                return (
                  <tr key={it.key} data-testid={`wizard-item-${it.key}`}>
                    <td className={cellCls}>
                      {isManual ? (
                        <TreatmentCombobox
                          value={it}
                          catalog={catalog}
                          onPick={(c) => {
                            pushRecent(c.id);
                            const label = c.variant ? `${c.name} · ${c.variant}` : c.name;
                            const requires_tooth = TOOTH_REQUIRED_CATEGORIES.has(c.category);
                            onPatch(it.key, {
                              catalog_id: c.id, name: label, price: Number(c.cost) || 0,
                              category: c.category, requires_tooth,
                              tooth: requires_tooth ? it.tooth : [],
                            });
                          }}
                        />
                      ) : (
                        <>
                          <div className="text-sm font-semibold text-ink leading-snug">{it.name}</div>
                          <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-pine mt-1">From completed visit</div>
                        </>
                      )}
                    </td>
                    <td className={cellCls}>
                      {hideTooth ? (
                        <span className="text-xs text-ink-soft italic">Not tooth-specific</span>
                      ) : (
                        <ToothMultiSelectPortal
                          value={it.tooth}
                          onChange={(tooth) => onPatch(it.key, { tooth })}
                          autoFocus={isManual && it.catalog_id && it.tooth.length === 0}
                        />
                      )}
                    </td>
                    <td className={cellCls}>
                      <input type="number" min={1} value={it.qty}
                             onChange={(e) => onPatch(it.key, { qty: Number(e.target.value) })}
                             className="w-full rounded-lg border border-[#E8EAE9] px-2.5 py-2 text-sm text-right tabular-nums focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10" />
                    </td>
                    <td className={cellCls}>
                      <input type="number" min={0} value={it.price}
                             onChange={(e) => onPatch(it.key, { price: Number(e.target.value) })}
                             className="w-full rounded-lg border border-[#E8EAE9] px-2.5 py-2 text-sm text-right tabular-nums focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10" />
                    </td>
                    <td className={cellCls}>
                      <input type="number" min={0} max={100} value={it.discount_pct}
                             onChange={(e) => onPatch(it.key, { discount_pct: Number(e.target.value) })}
                             className="w-full rounded-lg border border-[#E8EAE9] px-2.5 py-2 text-sm text-right tabular-nums focus:border-pine focus:outline-none focus:ring-2 focus:ring-pine/10" />
                    </td>
                    <td className={`${cellCls} text-right font-mono font-semibold text-ink tabular-nums`}>{INR(total)}</td>
                    <td className={cellCls}>
                      <button onClick={() => onRemove(it.key)}
                              className="p-2 rounded-md text-ink-soft hover:text-red-600 hover:bg-red-50 transition-colors"
                              data-testid={`wizard-item-delete-${it.key}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/* ---------------- Portal-based popover primitive --------------------------
 * Renders `children` into document.body with fixed-position anchoring to the
 * `anchorRef` element.  Escapes any overflow:hidden or transform ancestor —
 * including the invoice modal's scroll container.
 * -------------------------------------------------------------------------- */
function Popover({ anchorRef, open, onClose, minWidth = 260, maxWidth = 340, children }) {
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // Prefer opening below; flip above when there isn't room.
      const spaceBelow = vh - r.bottom;
      const openUp     = spaceBelow < 260 && r.top > spaceBelow;
      setPos({
        left: r.left,
        top:  openUp ? undefined : r.bottom + 4,
        bottom: openUp ? vh - r.top + 4 : undefined,
        width: Math.max(r.width, minWidth),
      });
    };
    measure();
    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", measure);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", measure);
    };
  }, [open, anchorRef, minWidth]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        anchorRef.current && !anchorRef.current.contains(e.target)
      ) onClose();
    };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !pos) return null;
  return createPortal(
    <div
      ref={popRef}
      role="listbox"
      data-viyora-popover
      className="fixed z-[10000] pointer-events-auto rounded-xl border border-[#EFEADC] bg-white shadow-[0_20px_60px_-16px_rgba(17,24,39,0.24)] animate-in fade-in slide-in-from-top-1"
      style={{ left: pos.left, top: pos.top, bottom: pos.bottom, minWidth: pos.width, maxWidth }}
    >
      {children}
    </div>,
    document.body,
  );
}


/* ---------------- TreatmentCombobox — strict select-from-master ---------- */
function TreatmentCombobox({ value, catalog, onPick }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(0);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);

  const recentIds = useMemo(() => readRecent(), [open]);
  const catalogById = useMemo(
    () => Object.fromEntries((catalog || []).map((c) => [c.id, c])),
    [catalog],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const active = (catalog || []).filter((c) => c.active !== false);
    const matches = q
      ? active.filter((c) => {
          const label = c.variant ? `${c.name} · ${c.variant}` : c.name;
          return label.toLowerCase().includes(q) || c.category?.toLowerCase().includes(q);
        })
      : active;
    // Recent-first when there is no query.
    if (!q) {
      const recentSet = new Set(recentIds);
      const recent = recentIds.map((id) => catalogById[id]).filter(Boolean);
      const rest = matches.filter((c) => !recentSet.has(c.id));
      return { recent, rest: rest.slice(0, 20) };
    }
    return { recent: [], rest: matches.slice(0, 30) };
  }, [query, catalog, recentIds, catalogById]);

  const flatList = useMemo(() => [...filtered.recent, ...filtered.rest], [filtered]);

  useEffect(() => { setHoverIdx(0); }, [query, open]);

  const onKey = (e) => {
    if (!open) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHoverIdx((i) => Math.min(i + 1, flatList.length - 1)); }
    if (e.key === "ArrowUp")   { e.preventDefault(); setHoverIdx((i) => Math.max(i - 1, 0)); }
    if (e.key === "Enter" && flatList[hoverIdx]) {
      e.preventDefault();
      onPick(flatList[hoverIdx]);
      setQuery(""); setOpen(false);
      inputRef.current?.blur();
    }
    if (e.key === "Escape") { setOpen(false); }
  };

  const displayValue = value?.name || "";

  return (
    <div ref={anchorRef} className="relative">
      <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 bg-white transition ${
        open ? "border-pine ring-2 ring-pine/10" : "border-[#E8EAE9] hover:border-ink-faint"
      }`}>
        <Search className="w-3.5 h-3.5 text-ink-soft shrink-0" />
        <input
          ref={inputRef}
          value={open ? query : displayValue}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder={displayValue ? "Change treatment…" : "Search Treatment Master…"}
          className="flex-1 min-w-0 bg-transparent text-sm outline-none placeholder:text-ink-soft"
          data-testid="wizard-treatment-input"
        />
        {value?.category && (
          <span className="hidden md:inline-flex text-[10px] uppercase tracking-[0.12em] font-bold px-1.5 py-0.5 rounded bg-linen text-ink-mid">
            {value.category}
          </span>
        )}
      </div>

      <Popover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} minWidth={340} maxWidth={440}>
        <div className="max-h-80 overflow-y-auto py-1.5">
          {flatList.length === 0 && (
            <div className="px-4 py-3 text-sm text-ink-soft">No treatments match "{query}".</div>
          )}
          {filtered.recent.length > 0 && (
            <>
              <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] font-bold text-ink-soft">Recently used</div>
              {filtered.recent.map((c, i) => (
                <CatalogRow key={c.id} c={c} active={hoverIdx === i}
                            onHover={() => setHoverIdx(i)}
                            onPick={() => { onPick(c); setQuery(""); setOpen(false); }} />
              ))}
              {filtered.rest.length > 0 && (
                <div className="mt-1 px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] font-bold text-ink-soft border-t border-[#F0EBDD]">All treatments</div>
              )}
            </>
          )}
          {filtered.rest.map((c, i) => {
            const idx = filtered.recent.length + i;
            return (
              <CatalogRow key={c.id} c={c} active={hoverIdx === idx}
                          onHover={() => setHoverIdx(idx)}
                          onPick={() => { onPick(c); setQuery(""); setOpen(false); }} />
            );
          })}
        </div>
      </Popover>
    </div>
  );
}

function CatalogRow({ c, active, onHover, onPick }) {
  const label = c.variant ? `${c.name} · ${c.variant}` : c.name;
  return (
    <button type="button"
            onMouseEnter={onHover}
            onClick={onPick}
            data-testid={`wizard-catalog-hit-${c.id}`}
            className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-3 transition-colors ${
              active ? "bg-pine/10" : "hover:bg-linen"
            }`}>
      <div className="min-w-0">
        <div className="text-sm text-ink font-medium truncate">{label}</div>
        <div className="text-[11px] text-ink-mid">{c.category}</div>
      </div>
      <div className="font-mono text-sm text-ink tabular-nums shrink-0">{INR(c.cost)}</div>
    </button>
  );
}


/* ---------------- ToothMultiSelectPortal — FDI multi-select in a portal --- */
function ToothMultiSelectPortal({ value = [], onChange, autoFocus }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const anchorRef = useRef(null);
  useEffect(() => { if (autoFocus) setOpen(true); }, [autoFocus]);

  const shown = useMemo(() => {
    const q = query.trim();
    if (!q) return FDI_TEETH;
    return FDI_TEETH.filter((t) => t.includes(q));
  }, [query]);
  const set = new Set(value);
  const toggle = (t) => onChange(set.has(t) ? value.filter((x) => x !== t) : [...value, t]);

  return (
    <div ref={anchorRef}>
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="wizard-tooth-trigger"
              className={`w-full rounded-lg border px-3 py-2 text-left text-sm flex items-center justify-between gap-2 bg-white transition ${
                open ? "border-pine ring-2 ring-pine/10" : "border-[#E8EAE9] hover:border-ink-faint"
              }`}>
        <div className="min-w-0 flex-1">
          {value.length ? <ToothChips value={value} readOnly /> : <span className="text-ink-soft">Select tooth…</span>}
        </div>
        <ChevronDown className={`w-3.5 h-3.5 text-ink-soft shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <Popover anchorRef={anchorRef} open={open} onClose={() => setOpen(false)} minWidth={300} maxWidth={340}>
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2.5">
            <Search className="w-3.5 h-3.5 text-ink-soft" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search FDI number…"
              className="flex-1 text-sm outline-none placeholder:text-ink-soft bg-transparent"
              data-testid="wizard-tooth-search"
            />
          </div>
          <div className="text-[10px] uppercase tracking-widest font-semibold text-ink-soft mb-1.5">FDI 11–48 · multi-select</div>
          <div className="grid grid-cols-8 gap-1">
            {shown.map((t, i) => {
              const on = set.has(t);
              return (
                <button key={t} type="button" onClick={() => toggle(t)}
                        className={`text-[11px] rounded-md py-1.5 font-mono tabular-nums transition ${
                          on ? "bg-pine text-white shadow-sm"
                             : "bg-linen text-ink hover:bg-pine/10"
                        } ${i === 15 && !query ? "mb-1.5" : ""}`}
                        data-testid={`tooth-${t}`}>
                  {t}
                </button>
              );
            })}
          </div>
          {value.length > 0 && (
            <button type="button" onClick={() => onChange([])}
                    className="mt-3 w-full text-[11px] text-ink-mid hover:text-ink py-1.5 rounded-md hover:bg-linen">
              Clear all
            </button>
          )}
        </div>
      </Popover>
    </div>
  );
}


function ToothChips({ value, readOnly }) {
  if (!value?.length) return null;
  return (
    <div className="inline-flex flex-wrap gap-1">
      {value.map((t) => (
        <span key={t} className={`inline-flex items-center text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded-md ${
          readOnly ? "bg-linen text-ink border border-[#E8EAE9]" : "bg-pine text-white"
        }`}>{t}</span>
      ))}
    </div>
  );
}


